<template>
  <div class="date-picker-wrapper">
    <input
      ref="dateInput"
      type="text"
      :placeholder="$t('experiences.selectDate')"
      class="form-control date-picker-input"
      :value="formattedDate"
      @input="handleInput"
      required
    />
    <svg class="date-picker-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
    </svg>
  </div>
</template>

<script>
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';

// Definir traduções manualmente para garantir controle total
const Portuguese = {
  weekdays: {
    shorthand: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    longhand: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']
  },
  months: {
    shorthand: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    longhand: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
  },
  firstDayOfWeek: 1,
  rangeSeparator: ' até ',
  weekAbbreviation: 'Sem',
  scrollTitle: 'Scroll para incrementar',
  toggleTitle: 'Clique para alternar',
  amPM: ['AM', 'PM'],
  yearAriaLabel: 'Ano',
  monthAriaLabel: 'Mês',
  hourAriaLabel: 'Hora',
  minuteAriaLabel: 'Minuto',
  time_24hr: false
};

const English = {
  weekdays: {
    shorthand: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    longhand: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  },
  months: {
    shorthand: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    longhand: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  },
  firstDayOfWeek: 0,
  rangeSeparator: ' to ',
  weekAbbreviation: 'Wk',
  scrollTitle: 'Scroll to increment',
  toggleTitle: 'Click to toggle',
  amPM: ['AM', 'PM'],
  yearAriaLabel: 'Year',
  monthAriaLabel: 'Month',
  hourAriaLabel: 'Hour',
  minuteAriaLabel: 'Minute',
  time_24hr: false
};

export default {
  name: 'DatePicker',
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      flatpickrInstance: null
    };
  },
  computed: {
    formattedDate() {
      if (!this.value) return '';
      
      // Formatar a data para exibição baseado no idioma
      if (this.$i18n.locale === 'en') {
        // Inglês: YYYY-MM-DD (já está no formato correto)
        return this.value;
      } else {
        // Português: DD/MM/YYYY
        const parts = this.value.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return this.value;
      }
    },
    locale() {
      return this.$i18n.locale === 'en' ? English : Portuguese;
    },
    dateFormat() {
      // Formato para o Flatpickr
      return this.$i18n.locale === 'en' ? 'Y-m-d' : 'd/m/Y';
    }
  },
  mounted() {
    this.initFlatpickr();
  },
  beforeDestroy() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
  },
  watch: {
    '$i18n.locale'() {
      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
        this.$nextTick(() => {
          this.initFlatpickr();
        });
      }
    },
    value(newVal) {
      if (this.flatpickrInstance && newVal) {
        // Converter para formato correto baseado no idioma
        let dateToSet = newVal;
        if (this.$i18n.locale === 'pt' && newVal.includes('-') && !newVal.includes('/')) {
          // Converter YYYY-MM-DD para DD/MM/YYYY para exibição
          const parts = newVal.split('-');
          if (parts.length === 3) {
            dateToSet = `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
        }
        if (dateToSet !== this.flatpickrInstance.input.value) {
          this.flatpickrInstance.setDate(dateToSet, false);
        }
      }
    }
  },
  methods: {
    initFlatpickr() {
      const locale = this.locale;
      
      // Adicionar traduções para botões customizados
      const customLocale = {
        ...locale,
        clearLabel: this.$i18n.locale === 'en' ? 'Clear' : 'Limpar',
        todayLabel: this.$i18n.locale === 'en' ? 'Today' : 'Hoje'
      };
      
      // Converter valor para formato YYYY-MM-DD se necessário (para português)
      let defaultDate = this.value || null;
      if (defaultDate && this.$i18n.locale === 'pt' && defaultDate.includes('/')) {
        // Converter DD/MM/YYYY para YYYY-MM-DD
        const parts = defaultDate.split('/');
        if (parts.length === 3) {
          defaultDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }
      
      this.flatpickrInstance = flatpickr(this.$refs.dateInput, {
        dateFormat: this.dateFormat,
        locale: customLocale,
        defaultDate: defaultDate,
        onChange: (selectedDates, dateStr) => {
          // Converter para formato YYYY-MM-DD para armazenamento interno
          let formattedValue = dateStr;
          if (this.$i18n.locale === 'pt' && dateStr.includes('/')) {
            // Converter DD/MM/YYYY para YYYY-MM-DD
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              formattedValue = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
          }
          this.$emit('input', formattedValue);
        }
      });
    },
    handleInput(event) {
      this.$emit('input', event.target.value);
    }
  }
};
</script>

<style scoped>
.date-picker-wrapper {
  position: relative;
  width: 100%;
}

.date-picker-input {
  cursor: pointer;
  padding-right: 2.5rem;
  width: 100%;
}

.date-picker-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  width: 16px;
  height: 16px;
  z-index: 10;
  display: block !important;
  fill: currentColor;
}
</style>

<style>
/* Estilos globais para o calendário Flatpickr - precisa ser global porque o calendário é renderizado fora do componente */
.flatpickr-calendar {
  width: 310px !important;
  font-size: 12px !important;
  box-shadow: 0 3px 13px rgba(0, 0, 0, 0.08) !important;
  min-width: 310px !important;
}

.flatpickr-months {
  padding: 4px 0 !important;
  min-height: 28px !important;
}

.flatpickr-month {
  height: 28px !important;
  line-height: 28px !important;
  font-size: 13px !important;
  padding: 0 2px !important;
}

.flatpickr-month .flatpickr-current-month {
  font-size: 13px !important;
  padding: 0 4px !important;
}

.flatpickr-month .flatpickr-monthDropdown-months {
  font-size: 13px !important;
  padding: 0 2px !important;
}

.flatpickr-prev-month,
.flatpickr-next-month {
  height: 28px !important;
  line-height: 28px !important;
  padding: 0 8px !important;
  width: 28px !important;
}

.flatpickr-weekdays {
  height: 24px !important;
  margin-top: 0 !important;
}

.flatpickr-weekday {
  font-size: 10px !important;
  height: 24px !important;
  line-height: 24px !important;
  font-weight: 500 !important;
  padding: 0 1px !important;
  width: calc(100% / 7) !important;
  min-width: calc(100% / 7) !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

.flatpickr-days {
  padding: 4px !important;
}

.flatpickr-day {
  height: 24px !important;
  line-height: 24px !important;
  font-size: 11px !important;
  margin: 1px !important;
  border-radius: 2px !important;
  width: calc((100% - 14px) / 7) !important;
  min-width: calc((100% - 14px) / 7) !important;
  flex: 0 0 calc((100% - 14px) / 7) !important;
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange {
  height: 24px !important;
  line-height: 24px !important;
  background: var(--primary-color, #B1D1A0) !important;
  border-color: var(--primary-color, #B1D1A0) !important;
  color: white !important;
}

.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover {
  background: var(--primary-color, #B1D1A0) !important;
  border-color: var(--primary-color, #B1D1A0) !important;
}

.flatpickr-day:hover {
  background: rgba(177, 209, 160, 0.2) !important;
  border-color: var(--primary-color, #B1D1A0) !important;
}

.flatpickr-day.today {
  border-color: var(--primary-color, #B1D1A0) !important;
}

.flatpickr-day.today:hover {
  background: rgba(177, 209, 160, 0.3) !important;
}
</style>

